import { useState } from "react";
import { Activity, CalendarEvent } from "../types/training";
import { estimateEventTSS, weekBarTotal } from "./chartMetrics";

// ── ISO week helpers ──────────────────────────────────────────────────────────

function isoWeekKey(dateStr: string): string {
  const d = new Date(dateStr.slice(0, 10) + "T12:00:00");
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((utc.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function weekLabel(key: string): string {
  const m = key.match(/W(\d+)$/);
  return m ? `V${parseInt(m[1])}` : key;
}

function buildWeekKeys(today: Date, past: number, future: number): string[] {
  const keys: string[] = [];
  for (let i = -past; i <= future; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i * 7);
    const k = isoWeekKey(d.toISOString().slice(0, 10));
    if (!keys.includes(k)) keys.push(k);
  }
  return keys.sort();
}

// ── Progress color ────────────────────────────────────────────────────────────

/** Returns fill (and optional stroke) for a bar based on week-over-week change.
 *  >10% increase → red (overreach), 0–10% → green (ideal), <0% → yellow (recovery).
 *  ghost=true means a planned/outline bar; ghost=false means a solid actual bar. */
function progressFill(
  current: number,
  prev: number,
  ghost: boolean
): { fill: string; stroke?: string } {
  if (prev <= 0 || current <= 0) {
    return ghost
      ? { fill: "rgba(26,26,26,0.10)", stroke: "rgba(26,26,26,0.32)" }
      : { fill: "rgba(26,26,26,0.78)" };
  }
  const ratio = current / prev;
  if (ratio > 1.10)  // >10% jump → overreach risk
    return ghost
      ? { fill: "rgba(220,60,60,0.15)",   stroke: "rgba(220,60,60,0.65)" }
      : { fill: "rgba(220,80,80,0.82)" };
  if (ratio >= 1.00) // 0–10% increase → progressive overload
    return ghost
      ? { fill: "rgba(60,200,110,0.15)",  stroke: "rgba(60,200,110,0.65)" }
      : { fill: "rgba(70,200,110,0.82)" };
  // below previous → recovery week
  return ghost
    ? { fill: "rgba(230,180,50,0.15)",    stroke: "rgba(230,180,50,0.65)" }
    : { fill: "rgba(230,180,50,0.82)" };
}

// ── Chart data ────────────────────────────────────────────────────────────────

interface Bucket {
  key: string; label: string;
  actual: number; planned: number;
  isCurrent: boolean; isPast: boolean;
}

// ── SVG bar chart (no external deps) ─────────────────────────────────────────

const VW = 800;
const VH = 140;
const PAD_L = 4; const PAD_R = 4; const PAD_T = 16; const PAD_B = 20;
const CHART_W = VW - PAD_L - PAD_R;
const CHART_H = VH - PAD_T - PAD_B;

interface TooltipState { x: number; y: number; bucket: Bucket; }

export function WeeklyTSSChart({ activities, events }: {
  activities: Activity[];
  events: CalendarEvent[];
}) {
  const [tip, setTip] = useState<TooltipState | null>(null);

  const today = new Date();
  const currentKey = isoWeekKey(today.toISOString().slice(0, 10));
  const weekKeys = buildWeekKeys(today, 8, 8);

  const buckets: Bucket[] = weekKeys.map(key => ({
    key, label: weekLabel(key),
    actual: 0, planned: 0,
    isCurrent: key === currentKey,
    isPast: key < currentKey,
  }));
  const idx: Record<string, number> = {};
  buckets.forEach((b, i) => { idx[b.key] = i; });

  // Actual TSS from completed activities
  for (const a of activities) {
    if (!a.icu_training_load) continue;
    const k = isoWeekKey(a.start_date_local);
    if (idx[k] !== undefined) buckets[idx[k]].actual += a.icu_training_load;
  }

  // Planned TSS from events — use explicit load, or estimate from description
  for (const e of events) {
    const k = isoWeekKey(e.start_date_local);
    if (idx[k] === undefined || buckets[idx[k]].isPast) continue;
    const tss = estimateEventTSS(e); // prefers explicit load, else estimates
    if (tss > 0) buckets[idx[k]].planned += tss;
  }

  const n = buckets.length;
  const colW = CHART_W / n;
  const barW = colW * 0.55;
  // The displayed bar height is the week's TOTAL load: a past week is its actual,
  // a future week its planned, the current week actual + planned stacked — so the
  // current week is comparable in height to a fully-completed week.
  const maxVal = Math.max(...buckets.map(b => weekBarTotal(b.actual, b.planned, b.isPast)), 1);

  const bx = (i: number) => PAD_L + i * colW + (colW - barW) / 2;
  const bh = (v: number) => (v / maxVal) * CHART_H;
  const by = (v: number) => PAD_T + CHART_H - bh(v);

  return (
    <div className="tss-chart" onMouseLeave={() => setTip(null)}>
      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        style={{ width: "100%", height: 160, display: "block" }}
        onMouseLeave={() => setTip(null)}
      >
        {/* Baseline */}
        <line
          x1={PAD_L} y1={PAD_T + CHART_H}
          x2={VW - PAD_R} y2={PAD_T + CHART_H}
          stroke="rgba(26,26,26,0.07)" strokeWidth={1}
        />

        {buckets.map((b, i) => {
          const x = bx(i);
          const showPlanned = b.planned > 0 && !b.isPast;

          // Previous week's reference value for colour coding
          const prevBucket = i > 0 ? buckets[i - 1] : null;
          const prevRef = prevBucket
            ? (prevBucket.actual > 0 ? prevBucket.actual : prevBucket.planned)
            : 0;

          const actualColors  = progressFill(b.actual,  prevRef, false);
          // For planned bars, compare to previous week's actual (or planned if no actual)
          const plannedColors = progressFill(b.planned, prevRef, true);

          return (
            <g key={b.key}
              onMouseEnter={e => {
                const svg = (e.currentTarget.ownerSVGElement as SVGSVGElement);
                const rect = svg.getBoundingClientRect();
                const cx = rect.left + (x + barW / 2) / VW * rect.width;
                const cy = rect.top + PAD_T / VH * rect.height;
                setTip({ x: cx, y: cy, bucket: b });
              }}
            >
              {/* Actual bar */}
              {b.actual > 0 && (
                <rect
                  x={x} y={by(b.actual)}
                  width={barW} height={bh(b.actual)}
                  fill={b.isCurrent ? actualColors.fill.replace(/[\d.]+\)$/, "0.55)") : actualColors.fill}
                  rx={2}
                />
              )}

              {/* Planned bar (ghost) — stacked on top of the actual (current week)
                  or standalone from the baseline (future week, no actual yet). */}
              {showPlanned && (
                <rect
                  x={x} y={by(b.actual + b.planned)}
                  width={barW} height={bh(b.planned)}
                  fill={plannedColors.fill}
                  stroke={plannedColors.stroke}
                  strokeWidth={1}
                  rx={2}
                />
              )}

              {/* Week label */}
              <text
                x={x + barW / 2} y={VH - 3}
                textAnchor="middle"
                fontSize={b.isCurrent ? 9 : 8}
                fontWeight={b.isCurrent ? 700 : 400}
                fill={b.isCurrent ? "rgba(26,26,26,0.8)" : "rgba(26,26,26,0.3)"}
                fontFamily="inherit"
              >
                {b.label}
              </text>

              {/* Current week marker */}
              {b.isCurrent && (
                <line
                  x1={x + barW / 2} y1={PAD_T}
                  x2={x + barW / 2} y2={PAD_T + CHART_H}
                  stroke="rgba(26,26,26,0.12)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tip && (tip.bucket.actual > 0 || tip.bucket.planned > 0) && (
        <div className="tss-chart__tooltip" style={{ left: tip.x, top: tip.y }}>
          <div className="tss-chart__tooltip-label">{tip.bucket.label}</div>
          {tip.bucket.actual > 0 && (
            <div>Genomfört: <strong>{Math.round(tip.bucket.actual)}</strong></div>
          )}
          {tip.bucket.planned > 0 && (
            <div className="tss-chart__tooltip-planned">
              Planerat: <strong>{Math.round(tip.bucket.planned)}</strong>
              <span> (est.)</span>
            </div>
          )}
        </div>
      )}

      <div className="tss-chart__legend">
        <span className="tss-chart__legend-item tss-chart__legend-item--actual">Genomfört TSS</span>
        <span className="tss-chart__legend-item tss-chart__legend-item--planned">Planerat TSS (est.)</span>
        <span className="tss-chart__legend-sep" />
        <span className="tss-chart__legend-item tss-chart__legend-item--green">+0–10 %</span>
        <span className="tss-chart__legend-item tss-chart__legend-item--yellow">Vila / minskning</span>
        <span className="tss-chart__legend-item tss-chart__legend-item--red">&gt;+10 % (risk)</span>
      </div>
    </div>
  );
}
